// P3-006: Board state store for 7x5 Match-3 grid

import { create } from 'zustand';
import { ElementType } from '@/shared/types/common';
import type { Tile, TilePosition } from '../types/board';
import { BOARD_COLS, BOARD_ROWS, ALL_ELEMENTS } from '../data/board-constants';

export interface BoardStoreState {
  tiles: (Tile | null)[][];

  // Actions
  generateBoard: () => void;
  setTiles: (tiles: (Tile | null)[][]) => void;
  swapTiles: (pos1: TilePosition, pos2: TilePosition) => void;
  removeTiles: (positions: TilePosition[]) => void;
  applyGravity: () => void;
  lockTiles: (positions: TilePosition[]) => void;
  unlockTiles: () => void;
  setTileElement: (pos: TilePosition, element: ElementType) => void;
  getTile: (pos: TilePosition) => Tile | null;
  reset: () => void;
}

/** Check if a board position has 3-in-a-row horizontally or vertically */
function hasMatchAt(tiles: (Tile | null)[][], x: number, y: number): boolean {
  const tile = tiles[x]?.[y];
  if (!tile) return false;
  const elem = tile.element;

  // Check horizontal (look left)
  if (x >= 2) {
    const t1 = tiles[x - 1]?.[y];
    const t2 = tiles[x - 2]?.[y];
    if (t1?.element === elem && t2?.element === elem) return true;
  }

  // Check vertical (look up)
  if (y >= 2) {
    const t1 = tiles[x]?.[y - 1];
    const t2 = tiles[x]?.[y - 2];
    if (t1?.element === elem && t2?.element === elem) return true;
  }

  return false;
}

/** Generate a random element, optionally avoiding creating matches */
function randomElement(): ElementType {
  const idx = Math.floor(Math.random() * ALL_ELEMENTS.length);
  return ALL_ELEMENTS[idx]!;
}

/** Generate a board with no initial 3-matches */
function generateCleanBoard(): (Tile | null)[][] {
  const MAX_ATTEMPTS = 100;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const tiles: (Tile | null)[][] = [];

    for (let x = 0; x < BOARD_COLS; x++) {
      tiles[x] = [];
      for (let y = 0; y < BOARD_ROWS; y++) {
        // Try to pick an element that doesn't create a match
        let element: ElementType;
        let tries = 0;
        do {
          element = randomElement();
          const testTile: Tile = { element, x, y };
          tiles[x]![y] = testTile;
          tries++;
        } while (hasMatchAt(tiles, x, y) && tries < 50);

        tiles[x]![y] = { element, x, y };
      }
    }

    // Verify no matches exist
    let clean = true;
    for (let x = 0; x < BOARD_COLS && clean; x++) {
      for (let y = 0; y < BOARD_ROWS && clean; y++) {
        if (hasMatchAt(tiles, x, y)) {
          clean = false;
        }
      }
    }

    if (clean) return tiles;
  }

  // Fallback: generate a simple alternating pattern (guaranteed no matches)
  const tiles: (Tile | null)[][] = [];
  for (let x = 0; x < BOARD_COLS; x++) {
    tiles[x] = [];
    for (let y = 0; y < BOARD_ROWS; y++) {
      const elemIdx = (x + y * 2) % ALL_ELEMENTS.length;
      tiles[x]![y] = { element: ALL_ELEMENTS[elemIdx]!, x, y };
    }
  }
  return tiles;
}

function createEmptyBoard(): (Tile | null)[][] {
  const tiles: (Tile | null)[][] = [];
  for (let x = 0; x < BOARD_COLS; x++) {
    tiles[x] = [];
    for (let y = 0; y < BOARD_ROWS; y++) {
      tiles[x]![y] = null;
    }
  }
  return tiles;
}

export const useBoardStore = create<BoardStoreState>()((set, get) => ({
  tiles: createEmptyBoard(),

  generateBoard: () => {
    set({ tiles: generateCleanBoard() });
  },

  setTiles: (tiles: (Tile | null)[][]) => {
    set({ tiles });
  },

  swapTiles: (pos1: TilePosition, pos2: TilePosition) => {
    const { tiles } = get();
    const newTiles = tiles.map((col) => [...col]);

    const tile1 = newTiles[pos1.x]?.[pos1.y];
    const tile2 = newTiles[pos2.x]?.[pos2.y];

    if (!tile1 || !tile2) return;
    if (tile1.locked || tile2.locked) return;

    // Swap tiles and update coordinates
    newTiles[pos1.x]![pos1.y] = { ...tile2, x: pos1.x, y: pos1.y };
    newTiles[pos2.x]![pos2.y] = { ...tile1, x: pos2.x, y: pos2.y };

    set({ tiles: newTiles });
  },

  removeTiles: (positions: TilePosition[]) => {
    const { tiles } = get();
    const newTiles = tiles.map((col) => [...col]);

    for (const pos of positions) {
      const tile = newTiles[pos.x]?.[pos.y];
      if (tile && !tile.locked) {
        newTiles[pos.x]![pos.y] = null;
      }
    }

    set({ tiles: newTiles });
  },

  applyGravity: () => {
    const { tiles } = get();
    const newTiles: (Tile | null)[][] = [];

    for (let x = 0; x < BOARD_COLS; x++) {
      const col = tiles[x] ?? [];
      const newCol: (Tile | null)[] = new Array(BOARD_ROWS).fill(null);

      // Separate locked and non-locked tiles
      // Locked tiles stay in their position
      const lockedPositions = new Set<number>();
      for (let y = 0; y < BOARD_ROWS; y++) {
        const tile = col[y];
        if (tile?.locked) {
          newCol[y] = tile;
          lockedPositions.add(y);
        }
      }

      // Collect non-locked, non-null tiles (gravity: fall UP toward enemy at y=0)
      const fallingTiles: Tile[] = [];
      for (let y = 0; y < BOARD_ROWS; y++) {
        const tile = col[y];
        if (tile && !tile.locked) {
          fallingTiles.push(tile);
        }
      }

      // Place falling tiles from top down (y=0 first), skipping locked positions
      // This makes tiles "bubble up" toward the enemy
      let writeIdx = 0;
      let readIdx = 0;
      while (readIdx < fallingTiles.length && writeIdx < BOARD_ROWS) {
        if (lockedPositions.has(writeIdx)) {
          writeIdx++;
          continue;
        }
        newCol[writeIdx] = { ...fallingTiles[readIdx]!, x, y: writeIdx };
        writeIdx++;
        readIdx++;
      }

      // Fill remaining empty spots (bottom of board) with new random tiles
      // New tiles spawn from the player side (bottom)
      for (let y = writeIdx; y < BOARD_ROWS; y++) {
        if (lockedPositions.has(y)) continue;
        if (newCol[y] === null) {
          newCol[y] = { element: randomElement(), x, y };
        }
      }

      newTiles[x] = newCol;
    }

    set({ tiles: newTiles });
  },

  lockTiles: (positions: TilePosition[]) => {
    const { tiles } = get();
    const newTiles = tiles.map((col) => [...col]);

    for (const pos of positions) {
      const tile = newTiles[pos.x]?.[pos.y];
      if (tile) {
        newTiles[pos.x]![pos.y] = { ...tile, locked: true };
      }
    }

    set({ tiles: newTiles });
  },

  unlockTiles: () => {
    const { tiles } = get();
    const newTiles = tiles.map((col) =>
      col.map((tile) => (tile ? { ...tile, locked: false } : null)),
    );
    set({ tiles: newTiles });
  },

  setTileElement: (pos: TilePosition, element: ElementType) => {
    const { tiles } = get();
    const newTiles = tiles.map((col) => [...col]);
    const tile = newTiles[pos.x]?.[pos.y];
    if (tile) {
      newTiles[pos.x]![pos.y] = { ...tile, element };
    }
    set({ tiles: newTiles });
  },

  getTile: (pos: TilePosition) => {
    const { tiles } = get();
    return tiles[pos.x]?.[pos.y] ?? null;
  },

  reset: () => {
    set({ tiles: createEmptyBoard() });
  },
}));
